"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiChevronRight, HiCheck } from "react-icons/hi";
import { useLanguage } from "@/lib/i18n";
import Link from "next/link";
import { cn } from "@/lib/utils";

const COOKIE_CONSENT_KEY = "kinken_cookie_consent";

type CookieCategory = "necessary" | "preferences" | "statistics" | "marketing";

// Re-defining the component to ensure clean state and variable scope
export default function CookieConsent() {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selections, setSelections] = useState<Record<CookieCategory, boolean>>({
    necessary: true,
    preferences: false,
    statistics: false,
    marketing: false,
  });

  const content = language === "id" ? {
    title: "WEBSITE INI MENGGUNAKAN COOKIES",
    description: "Kami menggunakan cookies yang mutlak diperlukan untuk mengaktifkan fitur dasar situs ini agar berfungsi. Tunduk pada persetujuan sukarela Anda, kami juga ingin mengatur cookies opsional untuk tujuan meningkatkan konten situs, mempersonalisasi konten dan iklan sesuai minat Anda, serta mengukur penggunaan situs. Dengan mengklik 'Izinkan semua cookies' Anda menyetujui penggunaan cookies ini. Ataupun, Anda dapat menyesuaikan cookies dengan mengklik 'Izinkan pilihan di bawah'. Untuk info lebih lanjut, kunjungi ",
    privacyPolicy: "Kebijakan Privasi",
    buttons: {
      allowAll: "IZINKAN SEMUA COOKIES",
      allowSelection: "IZINKAN PILIHAN DI BAWAH",
      necessaryOnly: "GUNAKAN YANG PERLU SAJA",
    },
    categories: {
      necessary: "Wajib",
      preferences: "Preferensi",
      statistics: "Statistik",
      marketing: "Pemasaran",
    },
    showDetails: "Lihat detail",
  } : {
    title: "THIS WEBSITE USES COOKIES",
    description: "We use cookies that are strictly necessary to enable the basic features of this site to function. Subject to your voluntary consent, we would also like to set optional cookies for the purposes of improving site content, personalizing content, tailoring advertising to your interests, and measuring site usage. By clicking on 'Allow all cookies' you are agreeing to the use of these cookies. Alternately, you can customize the cookies by clicking on 'Allow selections below'. For more information on our use of cookies, please visit our ",
    privacyPolicy: "Privacy Policy",
    buttons: {
      allowAll: "ALLOW ALL COOKIES",
      allowSelection: "ALLOW SELECTIONS BELOW",
      necessaryOnly: "USE NECESSARY COOKIES ONLY",
    },
    categories: {
      necessary: "Necessary",
      preferences: "Preferences",
      statistics: "Statistics",
      marketing: "Marketing",
    },
    showDetails: "Show details",
  };

  const categoryDetails = language === "id" ? {
     necessary: "Cookies ini mutlak diperlukan untuk fungsi situs web dan tidak dapat dimatikan. Biasanya hanya diatur sebagai tanggapan atas tindakan Anda yang meminta layanan, seperti mengatur preferensi privasi, masuk, atau mengisi formulir.",
     preferences: "Cookies ini memungkinkan situs web untuk menyediakan fungsionalitas dan personalisasi yang ditingkatkan. Mereka mungkin diatur oleh kami atau oleh penyedia pihak ketiga yang layanannya telah kami tambahkan ke halaman kami.",
     statistics: "Cookies ini memungkinkan kami untuk menghitung kunjungan dan sumber lalu lintas sehingga kami dapat mengukur dan meningkatkan kinerja situs kami. Mereka membantu kami mengetahui halaman mana yang paling dan paling sedikit populer dan melihat bagaimana pengunjung bergerak di sekitar situs.",
     marketing: "Cookies ini mungkin diatur melalui situs kami oleh mitra periklanan kami. Mereka dapat digunakan oleh perusahaan-perusahaan tersebut untuk membangun profil minat Anda dan menampilkan iklan yang relevan di situs lain."
  } : {
     necessary: "These cookies are strictly necessary for the website to function and cannot be displayed. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.",
     preferences: "These cookies allow the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages.",
     statistics: "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site.",
     marketing: "These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites."
  };

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSave = (type: "all" | "selection" | "necessary") => {
    let finalSelections = { ...selections };
    
    if (type === "all") {
      finalSelections = { necessary: true, preferences: true, statistics: true, marketing: true };
    } else if (type === "necessary") {
      finalSelections = { necessary: true, preferences: false, statistics: false, marketing: false };
    }

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: true,
      timestamp: new Date().toISOString(),
      version: "1.0",
      type,
      categories: finalSelections
    }));
    
    // Dispatch event for other components to react
    window.dispatchEvent(new Event("cookie-consent-update"));

    setIsVisible(false);
  };

  const toggleSelection = (category: CookieCategory) => {
    if (category === "necessary") return;
    setSelections(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "tween", duration: 0.5, ease: "circOut" }}
          className="fixed bottom-0 left-0 right-0 z-[9999] shadow-[0_-10px_40px_rgba(0,0,0,0.1)] font-sans"
        >
          <div className="bg-white border-t border-gray-100">
            {/* Main Content Area */}
            <div className="container mx-auto px-6 py-8 md:py-10 max-w-7xl">
              <div className="max-w-6xl mx-auto space-y-8">
                
                {/* Text Section */}
                <div className="space-y-4">
                  <h3 className="text-brand-charcoal text-sm font-bold tracking-wider uppercase">
                    {content.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed text-justify md:text-left">
                    {content.description}
                    <Link href="/privacy-policy" className="underline hover:text-brand-gold transition-colors">
                      {content.privacyPolicy}
                    </Link>.
                  </p>
                </div>

                {/* Buttons Row */}
                <div className="flex flex-col md:flex-row items-center gap-4 w-full">
                  {/* Allow All - Solid Gold */}
                  <button
                    onClick={() => handleSave("all")}
                    className="w-full md:w-auto min-w-[200px] px-8 py-3 bg-[#A39171] hover:bg-[#8E7C5E] text-white text-xs font-bold tracking-widest uppercase transition-colors"
                  >
                    {content.buttons.allowAll}
                  </button>

                  {/* Allow Selection - Outline */}
                  <button
                    onClick={() => handleSave("selection")}
                    className="w-full md:w-auto min-w-[200px] px-8 py-3 border border-[#A39171] text-[#A39171] hover:bg-brand-cream text-xs font-bold tracking-widest uppercase transition-colors"
                  >
                    {content.buttons.allowSelection}
                  </button>

                  {/* Necessary Only - Text Link Style (Desktop) or Button (Mobile) */}
                  <button
                    onClick={() => handleSave("necessary")}
                    className="w-full md:w-auto md:ml-auto px-4 py-3 text-gray-400 hover:text-brand-charcoal text-xs font-bold tracking-widest uppercase transition-colors"
                  >
                    {content.buttons.necessaryOnly}
                  </button>
                </div>

              </div>
            </div>

            {/* Bottom Bar - Categories */}
            <div className="bg-[#F9F9F7] border-t border-gray-100 py-4 px-6 transition-all duration-300">
              <div className="container mx-auto max-w-7xl">
                <div className="max-w-6xl mx-auto">
                   <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      {/* Checkboxes Group */}
                      <div className="flex flex-wrap gap-x-8 gap-y-3">
                        {/* Necessary (Locked) */}
                        <label className="flex items-center gap-2 cursor-not-allowed opacity-70">
                          <div className="relative flex items-center justify-center w-5 h-5 bg-[#A39171] border border-[#A39171] rounded-sm">
                            <HiCheck size={12} className="text-white" />
                          </div>
                          <span className="text-gray-600 text-xs font-medium">{content.categories.necessary}</span>
                        </label>

                        {/* Other Categories */}
                        {(["preferences", "statistics", "marketing"] as CookieCategory[]).map((cat) => (
                          <label key={cat} className="flex items-center gap-2 cursor-pointer group">
                            <div 
                              className={cn(
                                "relative flex items-center justify-center w-5 h-5 border transition-colors rounded-sm",
                                selections[cat] 
                                  ? "bg-[#A39171] border-[#A39171]" 
                                  : "bg-white border-gray-300 group-hover:border-[#A39171]"
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleSelection(cat);
                              }}
                            >
                              {selections[cat] && <HiCheck size={12} className="text-white" />}
                            </div>
                            <span 
                              className="text-gray-600 text-xs font-medium group-hover:text-brand-charcoal transition-colors"
                              onClick={(e) => {
                                e.preventDefault();
                                toggleSelection(cat);
                              }}
                            >
                              {content.categories[cat]}
                            </span>
                          </label>
                        ))}
                      </div>

                      {/* Show Details Button */}
                      <button 
                        onClick={() => setShowDetails(!showDetails)}
                        className="flex items-center gap-1 text-gray-500 hover:text-brand-charcoal transition-colors text-xs ml-auto font-medium"
                      >
                        {content.showDetails}
                        <HiChevronRight size={14} className={cn("transition-transform", showDetails ? "rotate-90" : "")} />
                      </button>
                   </div>
                   
                   {/* Expanded Details Section */}
                   <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6 pb-2 border-t border-gray-200 mt-4">
                           {(["necessary", "preferences", "statistics", "marketing"] as CookieCategory[]).map((cat) => (
                             <div key={cat} className="space-y-2">
                               <h4 className="text-xs font-bold uppercase text-brand-charcoal">{content.categories[cat]}</h4>
                               <p className="text-xs text-gray-500 leading-relaxed text-justify">
                                  {categoryDetails[cat]}
                               </p>
                             </div>
                           ))}
                        </div>
                      </motion.div>
                    )}
                   </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
